import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { WithPermission, usePermissions } from '../../contexts/PermissionsContext';
import './Layout.css';
import logoImage from '../../assets/logo.png';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { hasPermission, hasAnyPermission, loading: permissions_loading } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const is_sales_agent =
    user?.role_name === 'Sales Agent' ||
    (user?.sales_agent_id != null && String(user.sales_agent_id).trim() !== '');
  const can_view_executive_dashboard = hasPermission('reports.executive_dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Default to narrow sidebar on production dashboard
  const [isNarrowSidebar, setIsNarrowSidebar] = useState(
    location.pathname === '/production-dashboard'
  );

  const isActive = (path: string) => {
    return location.pathname === path ? 'nav-link active' : 'nav-link';
  };

  const handleLogout = () => {
    logout();
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleSidebarWidth = () => {
    setIsNarrowSidebar(!isNarrowSidebar);
  };

  useEffect(() => {
    if (permissions_loading) return;
    const path = location.pathname;
    const on_executive_dashboard =
      path.startsWith('/reports/executive-dashboard') ||
      path.startsWith('/reports/comparison-dashboard');
    if (is_sales_agent && (path === '/dashboard' || path === '/production-dashboard')) {
      navigate(can_view_executive_dashboard ? '/reports' : '/products', { replace: true });
      return;
    }
    if (on_executive_dashboard && !can_view_executive_dashboard) {
      navigate(is_sales_agent ? '/products' : '/reports', { replace: true });
    }
  }, [is_sales_agent, permissions_loading, can_view_executive_dashboard, location.pathname, navigate]);

  return (
    <div className="app-container">
      {/* Mobile Header */}
      <div className="mobile-header">
        <button className="hamburger-btn" onClick={toggleMobileMenu}>
          <span></span>
          <span></span>
          <span></span>
        </button>
        <img src={logoImage} alt="Blooms Logo" className="mobile-header-logo" />
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && <div className="mobile-overlay" onClick={closeMobileMenu}></div>}

      <nav
        className={`sidebar ${isMobileMenuOpen ? 'sidebar-open' : ''} ${isNarrowSidebar ? 'sidebar-narrow' : ''}`}
      >
        <div className="sidebar-header">
          <div className="sidebar-header-content">
            <img src={logoImage} alt="Blooms Logo" className="sidebar-logo" />
          </div>
          <button
            className="sidebar-toggle-btn"
            onClick={toggleSidebarWidth}
            title={isNarrowSidebar ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isNarrowSidebar ? '→' : '←'}
          </button>
        </div>

        <ul className="nav-menu">
          {!is_sales_agent && (
            <li>
              <Link
                to="/dashboard"
                className={isActive('/dashboard')}
                onClick={closeMobileMenu}
                title="Dashboard"
              >
                📈 {!isNarrowSidebar && 'Dashboard'}
              </Link>
            </li>
          )}

          <WithPermission permission="page.products">
            <li>
              <Link
                to="/products"
                className={isActive('/products')}
                onClick={closeMobileMenu}
                title="Products"
              >
                🧃 {!isNarrowSidebar && 'Products'}
              </Link>
            </li>
          </WithPermission>

          <WithPermission permission="page.bulk_products">
            <li>
              <Link
                to="/bulk-products"
                className={isActive('/bulk-products')}
                onClick={closeMobileMenu}
                title="Bulk Products"
              >
                📦 {!isNarrowSidebar && 'Bulk Products'}
              </Link>
            </li>
          </WithPermission>

          <WithPermission permission="page.inventory">
            <li>
              <Link
                to="/inventory"
                className={isActive('/inventory')}
                onClick={closeMobileMenu}
                title="Inventory"
              >
                📋 {!isNarrowSidebar && 'Inventory'}
              </Link>
            </li>
          </WithPermission>

          <WithPermission permission="page.production_orders">
            <li>
              <Link
                to="/production-orders"
                className={isActive('/production-orders')}
                onClick={closeMobileMenu}
                title="Production Orders"
              >
                🏭 {!isNarrowSidebar && 'Production Orders'}
              </Link>
            </li>
          </WithPermission>

          <WithPermission permission="page.production_schedule">
            <li>
              <Link
                to="/production-schedule"
                className={isActive('/production-schedule')}
                onClick={closeMobileMenu}
                title="Production Schedule"
              >
                📅 {!isNarrowSidebar && 'Production Schedule'}
              </Link>
            </li>
          </WithPermission>

          {!is_sales_agent && (
            <li>
              <Link
                to="/production-dashboard"
                className={isActive('/production-dashboard')}
                onClick={closeMobileMenu}
                title="Production Dashboard"
              >
                📊 {!isNarrowSidebar && 'Production Dashboard'}
              </Link>
            </li>
          )}

          {((is_sales_agent && can_view_executive_dashboard) ||
            (!is_sales_agent &&
              (hasPermission('reports.sales_reports') ||
                hasPermission('reports.view') ||
                hasPermission('page.comparison_dashboard')))) && (
            <li>
              <Link
                to="/reports"
                className={isActive('/reports')}
                onClick={closeMobileMenu}
                title="Reports"
              >
                📈 {!isNarrowSidebar && 'Reports'}
              </Link>
            </li>
          )}

          {((user && user.role_id === 1) ||
            hasAnyPermission([
              'admin.user_management',
              'admin.audit_logs',
              'admin.production_lines',
              'admin.production_schedule',
              'admin.invoice_processing',
            ])) &&
            user &&
            user.role_id !== 7 &&
            user.role_name !== 'Production Admin' && (
              <li>
                <Link
                  to="/admin"
                  className={isActive('/admin')}
                  onClick={closeMobileMenu}
                  title="Admin"
                >
                  ⚙️ {!isNarrowSidebar && 'Admin'}
                </Link>
              </li>
            )}
        </ul>

        <div className="sidebar-footer">
          <div className="user-info">
            <p>
              <strong>{user?.username}</strong>
            </p>
            <p className="user-role">{user?.role_name || user?.role}</p>
          </div>
          <button onClick={handleLogout} className="btn btn-secondary logout-btn">
            {isNarrowSidebar ? '👋🏽' : 'Logout'}
          </button>
        </div>
      </nav>

      <main className={`main-content ${isNarrowSidebar ? 'main-content-narrow' : ''}`}>
        {children}
      </main>
    </div>
  );
};

export default Layout;
